// PropertyMap — the customer's property on Google Maps: map / satellite, Street
// View (the pegman and a "Street view" button), tilt and rotate on satellite, a
// marker on the geocoded address. 240px tall, expands to the full card.
//
// Where the pin comes from, in order: coordinates on the customer record (RT's
// leg: `latitude` / `longitude`, or `lat` / `lng`), the FieldRoutes summary's
// lat/lng, then a client-side geocode of the address through /maps/geocode/,
// cached per address in this browser so a card reopened costs nothing.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import api from '../../api/client';
import GoogleMap, { Marker } from './GoogleMap';
import Button from '../ui/button';

const CACHE_PREFIX = 'geo:';
const readCache = (address) => {
  try { const v = localStorage.getItem(CACHE_PREFIX + address); return v ? JSON.parse(v) : null; } catch { return null; }
};
const writeCache = (address, coords) => {
  try { localStorage.setItem(CACHE_PREFIX + address, JSON.stringify(coords)); } catch { /* private mode */ }
};

const finite = (v) => Number.isFinite(Number(v)) && Number(v) !== 0;

export function resolveKnownCoords(raw = {}, fr = null) {
  const pairs = [
    [raw.latitude, raw.longitude],
    [raw.lat, raw.lng],
    [raw.geo_lat, raw.geo_lng],
    [fr?.lat, fr?.lng],
  ];
  for (const [la, ln] of pairs) {
    if (finite(la) && finite(ln)) return { lat: Number(la), lng: Number(ln), source: la === raw.latitude || la === raw.lat || la === raw.geo_lat ? 'record' : 'fieldroutes' };
  }
  return null;
}

export default function PropertyMap({ address, known = null, height = 240, expandedHeight = 480, className = '' }) {
  const [coords, setCoords] = useState(known || null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [streetOpen, setStreetOpen] = useState(false);
  const mapRef = useRef(null);
  const panoRef = useRef(null);

  useEffect(() => {
    if (known) { setCoords(known); return undefined; }
    if (!address) return undefined;
    const cached = readCache(address);
    if (cached) { setCoords(cached); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/maps/geocode/', { params: { address } });
        if (cancelled) return;
        const lat = Number(data?.result?.lat); const lng = Number(data?.result?.lng);
        if (finite(lat) && finite(lng)) { const c = { lat, lng, source: 'geocode' }; writeCache(address, c); setCoords(c); } else setFailed(true);
      } catch { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; };
  }, [address, known]);

  const onLoad = useCallback(({ target }) => {
    const map = target.getMap();
    mapRef.current = map;
    const g = window.google?.maps;
    if (!g || !map) return;
    // Tilt and rotate are satellite-only features; the controls show when they apply.
    map.setOptions({ rotateControl: true, tilt: map.getMapTypeId() === 'hybrid' ? 45 : 0 });
    g.event.addListener(map, 'maptypeid_changed', () => {
      map.setOptions({ tilt: map.getMapTypeId() === 'hybrid' ? 45 : 0 });
    });
    panoRef.current = map.getStreetView();
    g.event.addListener(panoRef.current, 'visible_changed', () => setStreetOpen(Boolean(panoRef.current?.getVisible())));
  }, []);

  const toggleStreet = () => {
    const pano = panoRef.current;
    if (!pano || !coords) return;
    if (pano.getVisible()) { pano.setVisible(false); return; }
    pano.setPosition({ lat: coords.lat, lng: coords.lng });
    pano.setPov({ heading: 0, pitch: 0 });
    pano.setVisible(true);
  };

  const h = open ? expandedHeight : height;
  const headline = useMemo(() => (coords?.source === 'geocode' ? 'Pin from the address' : coords?.source === 'fieldroutes' ? 'Pin from FieldRoutes' : ''), [coords]);

  if (!address && !known) return <div className="pmap pmap--empty">No property address on file — add the street on the left.</div>;

  return (
    <div className={`pmap ${open ? 'is-open' : ''} ${className}`}>
      <div className="pmap__bar">
        <span className="pmap__title">Property{headline ? <span className="pmap__src"> · {headline}</span> : null}</span>
        <span className="pmap__tools">
          {coords ? <Button size="sm" variant={streetOpen ? 'secondary' : 'ghost'} onClick={toggleStreet} aria-pressed={streetOpen}>{streetOpen ? 'Back to map' : 'Street view'}</Button> : null}
          <Button size="sm" variant="ghost" iconOnly aria-label={open ? 'Shrink map' : 'Expand map'} onClick={() => setOpen((o) => !o)} icon={open ? <Minimize2 size={15} /> : <Maximize2 size={15} />} />
        </span>
      </div>
      {coords ? (
        <div className="pmap__canvas" style={{ height: h }}>
          <GoogleMap key={open ? 'big' : 'small'} latitude={coords.lat} longitude={coords.lng} zoom={18} streetViewControl onLoad={onLoad} style={{ minHeight: h, height: h, borderRadius: 'var(--radius-md)' }}>
            <Marker latitude={coords.lat} longitude={coords.lng}>
              <div className="pmap__pin" />
            </Marker>
          </GoogleMap>
        </div>
      ) : (
        <div className="pmap__state" style={{ height }}>{failed ? 'Couldn’t place this address on the map.' : 'Placing the address…'}</div>
      )}
    </div>
  );
}
